import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section, StatusBadge, Avatar } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UploadSimple, DownloadSimple, PencilSimple, Trash as TrashIcon, PaperPlaneTilt, IdentificationCard, Camera, Plus } from "@phosphor-icons/react";

const EMPTY_EDIT = { id: "", name: "", email: "", department_id: "", team_id: "", reporting_admin_id: "", designation: "", role: "employee", _origRole: "employee", employee_id: "", phone: "", joining_date: "" };
const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", employee: "Employee" };

const WeekHours = ({ h }) => (
  <div className="min-w-[80px]">
    <div className="text-sm font-semibold text-[#1F4E8C]">{h}h</div>
    <div className="h-1 bg-[#F0F0EE] mt-1"><div className="h-1 bg-[#FF5500]" style={{ width: `${Math.min(100, (h / 40) * 100)}%` }} /></div>
  </div>
);

const LatestTs = ({ s, current }) => {
  if (!s?.latest_status) return <span className="text-xs text-neutral-400">Not submitted</span>;
  return (
    <div>
      <StatusBadge status={s.latest_status} direct={s.direct} />
      <div className="text-[11px] text-neutral-500 mt-0.5">{s.latest_week === current ? "This week" : `Week of ${s.latest_week}`}</div>
    </div>
  );
};

function TeamAdminCard({ admin, teams, members, onOpen }) {
  if (!admin) return null;
  const active = members.filter((m) => m.status !== "inactive").length;
  const info = [
    ["Email", admin.email], ["Employee ID", admin.employee_id], ["Designation", admin.designation],
    ["Department", admin.department_name || admin.department], ["Phone", admin.phone],
    ["Teams Led", teams.map((t) => t.name).join(", ")],
  ];
  return (
    <div className="bg-white border border-[#E5E5E5] border-l-4 border-l-[#FF5500] p-5 mb-6" data-testid="team-admin-card">
      <div className="flex items-start gap-4 flex-wrap">
        <Avatar name={admin.name} photo={admin.profile_photo} size={56} />
        <div className="flex-1 min-w-[220px]">
          <div className="eyebrow text-[#FF5500]">Team Admin</div>
          <div className="text-xl font-head font-bold text-[#1F4E8C]" data-testid="team-admin-name">{admin.name}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 mt-3 text-sm">
            {info.map(([k, v]) => (
              <div key={k}><div className="eyebrow text-neutral-500">{k}</div><div className="truncate">{v || "—"}</div></div>
            ))}
          </div>
        </div>
        <div className="flex gap-6 text-center">
          <div><div className="text-2xl font-bold text-[#1F4E8C]" data-testid="team-member-count">{members.length}</div><div className="eyebrow">Members</div></div>
          <div><div className="text-2xl font-bold text-emerald-600" data-testid="team-active-count">{active}</div><div className="eyebrow">Active</div></div>
        </div>
      </div>
      <button onClick={() => onOpen(admin)} className="mt-4 text-xs underline text-[#1F4E8C] hover:text-[#FF5500]" data-testid="team-admin-personal-info">
        View my personal info
      </button>
    </div>
  );
}

function OrgSelects({ value, onChange, depts, teams, admins, prefix }) {
  const teamsFor = value.department_id ? teams.filter((t) => t.department_id === value.department_id) : teams;
  const pickTeam = (e) => {
    const t = teams.find((x) => x.id === e.target.value);
    onChange({ ...value, team_id: e.target.value, ...(t?.department_id ? { department_id: t.department_id } : {}) });
  };
  const pickDept = (e) => {
    const keep = teams.find((x) => x.id === value.team_id)?.department_id === e.target.value;
    onChange({ ...value, department_id: e.target.value, team_id: keep ? value.team_id : "" });
  };
  const sel = (k, label, opts, span, handler) => (
    <div className={span ? "col-span-2" : ""}>
      <div className="eyebrow mb-1">{label}</div>
      <select value={value[k] || ""} onChange={handler || ((e) => onChange({ ...value, [k]: e.target.value }))}
        className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid={`${prefix}-${k}`}>
        <option value="">—</option>
        {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </div>
  );
  return (
    <>
      {sel("department_id", "Department", depts.map((d) => ({ id: d.id, label: d.name })), false, pickDept)}
      {sel("team_id", "Team", teamsFor.map((t) => ({ id: t.id, label: t.name })), false, pickTeam)}
      {sel("reporting_admin_id", "Reporting Admin", admins.filter((a) => a.id !== value.id).map((a) => ({ id: a.id, label: `${a.name} (${ROLE_LABEL[a.role]}) · ${a.email}` })), true)}
    </>
  );
}

// Personal-information field groups (mirror the employee's own MyProfile view)
const PI_BASIC = [
  { label: "Date of Birth", k: "date_of_birth", type: "date" },
  { label: "Gender", k: "gender" },
  { label: "Marital Status", k: "marital_status" },
  { label: "Nationality", k: "nationality" },
];
const PI_CONTACT = [
  { label: "Personal Email", k: "personal_email", type: "email" },
  { label: "Mobile Number", k: "mobile_number" },
  { label: "Alternate Phone", k: "alternate_phone" },
  { label: "City", k: "city" },
  { label: "State", k: "state" },
  { label: "Country", k: "country" },
  { label: "Postal Code", k: "postal_code" },
];
const PI_EMP = [
  { label: "Work Location", k: "work_location" },
  { label: "Office Location", k: "office_location" },
  { label: "Probation End Date", k: "probation_end_date", type: "date" },
  { label: "Confirmation Date", k: "confirmation_date", type: "date" },
];
const EMPLOYMENT_TYPES = ["Full Time", "Part Time", "Contract", "Intern", "Consultant"];

// Generate a reasonably strong random temporary password using the browser's CSPRNG.
function genTempPassword(len = 12) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const extras = "!@#$%&*";
  const buf = new Uint32Array(len);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  let out = "";
  for (let i = 0; i < len - 2; i++) out += alphabet[buf[i] % alphabet.length];
  out += extras[buf[len - 2] % extras.length];
  out += "23456789"[buf[len - 1] % 8];
  return out;
}

const BLANK_FORM = () => ({
  name: "", email: "", password: genTempPassword(),
  department_id: "", team_id: "", reporting_admin_id: "", designation: "", role: "employee", employee_id: "",
});

export default function AdminEmployees() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [confirmSuper, setConfirmSuper] = useState(null); // { name, run }
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [depts, setDepts] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [snap, setSnap] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [piOpen, setPiOpen] = useState(false);
  const [piUser, setPiUser] = useState(null);
  const [piData, setPiData] = useState({});
  const [piBank, setPiBank] = useState({});
  const [piStatutory, setPiStatutory] = useState({});
  const [piCanEditPayroll, setPiCanEditPayroll] = useState(false);
  const [piEmergency, setPiEmergency] = useState([]);
  const [piNewEmer, setPiNewEmer] = useState({ name: "", relationship: "", phone: "", email: "" });
  const [piPhoto, setPiPhoto] = useState(null);
  const [piLoading, setPiLoading] = useState(false);
  const [piSaving, setPiSaving] = useState(false);
  const [piUploading, setPiUploading] = useState(false);

  const load = async () => {
    try { const r = await api.get("/users"); setUsers(r.data); } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isSuper) {
      Promise.all([api.get("/teams"), api.get("/departments")])
        .then(([t, d]) => { setTeams(t.data || []); setDepts(d.data || []); })
        .catch((e) => toast.error(formatErr(e)));
    } else if (user) {
      api.get("/teams").then((t) => setTeams(t.data || [])).catch((e) => toast.error(formatErr(e)));
      api.get("/users/team-snapshot").then((r) => setSnap(r.data)).catch((e) => toast.error(formatErr(e)));
    }
  }, [isSuper, user]);
  const admins = users.filter((u) => (u.role === "admin" || u.role === "super_admin") && u.status !== "inactive");

  const create = async () => {
    try {
      const r = await api.post("/users", form);
      if (r.data?.welcome_email_sent) {
        toast.success("Employee added · welcome email sent");
      } else if (r.data?.welcome_email_error) {
        toast.warning(`Employee added · welcome email could not be sent: ${r.data.welcome_email_error}`);
      } else {
        toast.success("Employee added");
      }
      setOpen(false);
      setForm(BLANK_FORM());  // rotate the auto-generated temp password for the next add
      load();
    }
    catch (e) { toast.error(formatErr(e)); }
  };
  const onCreate = () => {
    if (form.role === "super_admin") setConfirmSuper({ name: form.name || form.email || "this user", run: create });
    else create();
  };
  const toggle = async (u) => {
    try {
      await api.post(`/users/${u.id}/${u.status === "active" ? "deactivate" : "activate"}`);
      toast.success("Updated"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const reset = async (u) => {
    const pw = prompt(`New password for ${u.name}:`);
    if (!pw) return;
    try { await api.post(`/users/${u.id}/reset-password`, { new_password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openEdit = (u) => {
    setEditForm({
      id: u.id,
      name: u.name || "",
      email: u.email || "",
      department_id: u.department_id || depts.find((d) => d.name === u.department)?.id || "",
      team_id: u.team_id || "",
      reporting_admin_id: u.reporting_admin_id || "",
      designation: u.designation || "",
      role: u.role || "employee",
      _origRole: u.role || "employee",
      employee_id: u.employee_id || "",
      phone: u.phone || "",
      joining_date: u.joining_date || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const { id, email, _origRole, ...payload } = editForm;
    try {
      await api.patch(`/users/${id}`, payload);
      toast.success("Employee updated");
      setEditOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const onSaveEdit = () => {
    if (editForm.role === "super_admin" && editForm._origRole !== "super_admin")
      setConfirmSuper({ name: editForm.name || editForm.email || "this user", run: saveEdit });
    else saveEdit();
  };

  const openProfile = async (u) => {
    setPiUser(u);
    setPiOpen(true);
    setPiLoading(true);
    setPiData({}); setPiBank({}); setPiStatutory({}); setPiEmergency([]);
    setPiCanEditPayroll(false); setPiPhoto(u.profile_photo || null);
    setPiNewEmer({ name: "", relationship: "", phone: "", email: "" });
    try {
      const r = await api.get(`/profile/${u.id}`);
      setPiData(r.data.personal || {});
      setPiBank(r.data.bank || {});
      setPiStatutory(r.data.statutory || {});
      setPiEmergency(r.data.emergency_contacts || []);
      setPiCanEditPayroll(!!r.data.can_edit_payroll);
      setPiPhoto((r.data.personal || {}).profile_photo || u.profile_photo || null);
    } catch (e) { toast.error(formatErr(e)); }
    setPiLoading(false);
  };

  const saveProfile = async () => {
    if (!piUser) return;
    const payload = {};
    Object.entries(piData).forEach(([k, v]) => {
      if (k.startsWith("_") || k === "user_id" || k === "updated_at" || k === "profile_photo") return;
      if (v !== "" && v !== null && v !== undefined) payload[k] = v;
    });
    setPiSaving(true);
    try {
      await api.put(`/profile/${piUser.id}`, payload);
      if (piCanEditPayroll) {
        const bankPayload = {
          bank_name: piBank.bank_name || "", account_holder: piBank.account_holder || "",
          account_number: piBank.account_number || "", ifsc_code: piBank.ifsc_code || "",
          branch: piBank.branch || "", account_type: piBank.account_type || "Savings", upi_id: piBank.upi_id || "",
        };
        await api.put(`/profile/${piUser.id}/bank`, bankPayload);
        const statPayload = {};
        Object.entries(piStatutory).forEach(([k, v]) => {
          if (k === "user_id" || k === "updated_at") return;
          if (v !== "" && v !== null && v !== undefined) statPayload[k] = v;
        });
        await api.put(`/profile/${piUser.id}/statutory`, statPayload);
      }
      toast.success("Personal information updated");
      setPiOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
    setPiSaving(false);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file || !piUser) return;
    setPiUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${piUser.id}/photo`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      setPiPhoto(data.profile_photo);
      toast.success("Photo updated");
      load();
    } catch (err) { toast.error(err.message); }
    e.target.value = ""; setPiUploading(false);
  };

  const addEmerAdmin = async () => {
    if (!piNewEmer.name || !piUser) return;
    try {
      const r = await api.post(`/profile/${piUser.id}/emergency-contacts`, piNewEmer);
      setPiEmergency([...piEmergency, r.data]);
      setPiNewEmer({ name: "", relationship: "", phone: "", email: "" });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const delEmerAdmin = async (cid) => {
    if (!piUser) return;
    try {
      await api.delete(`/profile/${piUser.id}/emergency-contacts/${cid}`);
      setPiEmergency(piEmergency.filter((c) => c.id !== cid));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`Deleted ${deleteTarget.name}`);
      setDeleteTarget(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const resendOne = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/resend-welcome`);
      if (r.data?.ok) {
        toast.success(`Welcome email resent to ${u.email}`);
      } else {
        toast.warning(`Resend failed: ${r.data?.error || "unknown"}`);
      }
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const resendAllFailed = async () => {
    const count = users.filter((u) => u.welcome_email_status === "failed" && u.status !== "inactive").length;
    if (count === 0) return toast.info("No failed welcome emails to resend");
    if (!window.confirm(`Resend welcome email to ${count} user(s) whose last send failed?\n\nEach will get a fresh random temporary password.`)) return;
    try {
      const r = await api.post("/users/resend-welcome-failed");
      toast.success(`${r.data.sent} sent · ${r.data.failed} still failing (of ${r.data.targets})`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadTemplate = async () => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/import/template`, {
      credentials: "include", headers: {},
    });
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = "gtes_employee_import_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/import`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      setImportResult(data);
      const emailBit = typeof data.emails_sent === "number" ? ` · ${data.emails_sent} welcome email${data.emails_sent === 1 ? "" : "s"} sent` : "";
      toast.success(`${data.created} added · ${data.skipped} skipped · ${data.errors.length} error(s)${emailBit}`);
      load();
    } catch (err) { toast.error(err.message); }
    e.target.value = ""; setImporting(false);
  };

  const me = !isSuper ? users.find((u) => u.id === user?.id) : null;
  const members = isSuper ? users : users.filter((u) => u.id !== user?.id);
  const filtered = members
    .filter((u) => !isSuper || roleFilter === "all" || u.role === roleFilter)
    .filter((u) => isSuper || teamFilter === "all" || u.team_id === teamFilter)
    .filter((u) => (u.name + u.email + (u.department_name || u.department || "") + (u.team_name || "") + (u.designation || "")).toLowerCase().includes(search.toLowerCase()));
  const chip = (active) => `px-3 h-9 text-xs font-semibold border ${active ? "bg-[#1F4E8C] text-white border-[#1F4E8C]" : "bg-white text-[#1F4E8C] border-[#E5E5E5]"}`;

  return (
    <div data-testid="admin-employees">
      <PageHeader overline="People" title={isSuper ? "Employees" : "My Team"}
        subtitle={isSuper ? "Create and manage Employees, Admins and Super Admins · assign department, team and reporting admin" : undefined}
        actions={
          <>
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none w-48" data-testid="search-employees" />
            {isSuper && (<>
            <Button size="sm" variant="outline" onClick={downloadTemplate} className="rounded-none h-10 text-xs" data-testid="dl-user-template">
              <DownloadSimple size={14} className="mr-1" /> Template
            </Button>
            <label className="inline-flex items-center h-10 px-3 border border-[#1F4E8C] text-[#1F4E8C] cursor-pointer hover:bg-[#1F4E8C] hover:text-white transition-colors text-xs font-semibold" data-testid="import-users">
              <UploadSimple size={14} className="mr-1" /> {importing ? "Importing…" : "Bulk Import"}
              <input type="file" accept=".xlsx" onChange={uploadFile} className="hidden" />
            </label>
            {(() => {
              const n = users.filter((u) => u.welcome_email_status === "failed" && u.status !== "inactive").length;
              if (n === 0) return null;
              return (
                <Button size="sm" onClick={resendAllFailed} data-testid="resend-all-failed"
                  className="rounded-none h-10 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold">
                  <PaperPlaneTilt size={14} className="mr-1" /> Resend Failed ({n})
                </Button>
              );
            })()}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="add-employee-btn">+ Add Employee</Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-lg" data-testid="new-employee-dialog">
                <DialogHeader><DialogTitle className="font-head">New Employee</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  {[["name", "Name"], ["email", "Email"], ["password", "Temporary Password"], ["employee_id", "Employee ID"], ["designation", "Designation"]].map(([k, label]) => (
                    <div key={k}>
                      <div className="eyebrow mb-1">{label}</div>
                      <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-none" data-testid={`emp-field-${k}`} />
                    </div>
                  ))}
                  <div>
                    <div className="eyebrow mb-1">Role</div>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="emp-field-role">
                      <option value="employee">Employee</option><option value="admin">Admin</option>{isSuper && <option value="super_admin">Super Admin</option>}
                    </select>
                  </div>
                  <OrgSelects value={form} onChange={setForm} depts={depts} teams={teams} admins={admins} prefix="emp-field" />
                  <Button onClick={onCreate} className="col-span-2 w-full rounded-none bg-[#FF5500]" data-testid="save-employee">Create</Button>
                </div>
              </DialogContent>
            </Dialog>
            </>)}
          </>
        } />

      {importResult && (
        <div className="mb-4 p-4 border border-[#1F4E8C] bg-[#F7F7F5]" data-testid="user-import-result">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow">Import summary</div>
              <div className="font-head text-xl font-semibold">
                {importResult.created} added
                <span className="text-neutral-500"> · {importResult.skipped} skipped</span>
                {importResult.errors?.length > 0 && <span className="text-red-600"> · {importResult.errors.length} error(s)</span>}
              </div>
              {typeof importResult.emails_sent === "number" && (
                <div className="text-xs text-neutral-600 mt-1" data-testid="user-import-email-summary">
                  Welcome emails: <span className="text-[#1F4E8C] font-semibold">{importResult.emails_sent} sent</span>
                  {importResult.emails_failed > 0 && <span className="text-red-600"> · {importResult.emails_failed} failed</span>}
                </div>
              )}
            </div>
            <button onClick={() => setImportResult(null)} className="text-sm text-neutral-500 hover:text-[#111]">×</button>
          </div>
          {importResult.errors?.length > 0 && (
            <div className="mt-3 text-xs space-y-1 max-h-40 overflow-y-auto">
              {importResult.errors.map((e, i) => <div key={i} className="text-red-600">Row {e.row}: {e.error}</div>)}
            </div>
          )}
        </div>
      )}

      {!isSuper && <TeamAdminCard admin={me} teams={teams} members={members} onOpen={openProfile} />}

      <div className="flex gap-2 mb-4 flex-wrap">
        {isSuper ? ["all", "super_admin", "admin", "employee"].map((r) => (
          <button key={r} onClick={() => setRoleFilter(r)} data-testid={`filter-${r}`} className={chip(roleFilter === r)}>
            {r === "all" ? "All" : ROLE_LABEL[r]}
          </button>
        )) : [{ id: "all", name: "All teams" }, ...teams].map((t) => (
          <button key={t.id} onClick={() => setTeamFilter(t.id)} data-testid={`team-filter-${t.id}`} className={chip(teamFilter === t.id)}>
            {t.name} ({t.id === "all" ? members.length : members.filter((m) => m.team_id === t.id).length})
          </button>
        ))}
      </div>

      <Section title={isSuper ? `${filtered.length} people` : `${filtered.length} team member${filtered.length === 1 ? "" : "s"}`}>
        <div className="overflow-x-auto">
        <table className="w-full table-dense min-w-[1100px]">
          <thead>
            <tr className="border-b-2 border-[#1F4E8C] text-left">
              <th className="eyebrow">Name</th><th className="eyebrow">Email</th><th className="eyebrow">Designation</th>
              <th className="eyebrow">Dept</th><th className="eyebrow">Team</th><th className="eyebrow">Role</th>
              {!isSuper && <th className="eyebrow" title={snap ? `${snap.week_start} → ${snap.week_end}` : ""}>This Week</th>}
              {!isSuper && <th className="eyebrow">Latest Timesheet</th>}
              <th className="eyebrow">Status</th><th className="eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-[#F0F0EE]" data-testid={`member-row-${u.id}`}>
                <td className="font-medium">
                  <div className="flex items-center gap-2.5">
                    <Avatar name={u.name} photo={u.profile_photo} size={32} />
                    <div>{u.name}<div className="text-xs text-neutral-500">{u.employee_id}</div></div>
                  </div>
                </td>
                <td>{u.email}</td>
                <td>{u.designation || "—"}</td>
                <td>{u.department_name || u.department || "—"}</td>
                <td>{u.team_name || "—"}</td>
                <td data-testid={`role-${u.id}`}>{ROLE_LABEL[u.role] || u.role}</td>
                {!isSuper && <td data-testid={`week-hours-${u.id}`}><WeekHours h={snap?.members[u.id]?.week_hours || 0} /></td>}
                {!isSuper && <td data-testid={`latest-ts-${u.id}`}><LatestTs s={snap?.members[u.id]} current={snap?.week_start} /></td>}
                <td><StatusBadge status={u.status} /></td>
                <td>
                  <div className="flex gap-3 items-center flex-wrap">
                    {isSuper && (
                    <button onClick={() => openEdit(u)} title="Edit" data-testid={`edit-${u.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#1F4E8C] hover:text-[#FF5500]">
                      <PencilSimple size={14} /> Edit
                    </button>
                    )}
                    <button onClick={() => openProfile(u)} title="Personal info" data-testid={`personal-info-${u.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#1F4E8C] hover:text-[#FF5500]">
                      <IdentificationCard size={14} /> Personal Info
                    </button>
                    {isSuper && (<>
                    <button onClick={() => toggle(u)} className="text-xs underline hover:text-[#FF5500]" data-testid={`toggle-${u.id}`}>
                      {u.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button onClick={() => reset(u)} className="text-xs underline hover:text-[#FF5500]">Reset PW</button>
                    {u.welcome_email_status === "failed" && u.status !== "inactive" && (
                      <button onClick={() => resendOne(u)} title={u.welcome_email_error || "Welcome email failed — click to resend"}
                        data-testid={`resend-welcome-${u.id}`}
                        className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900">
                        <PaperPlaneTilt size={14} /> Resend welcome
                      </button>
                    )}
                    <button onClick={() => setDeleteTarget(u)} title="Delete" data-testid={`delete-${u.id}`}
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800">
                      <TrashIcon size={14} /> Delete
                    </button>
                    </>)}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={isSuper ? 8 : 10} className="text-center py-8 text-neutral-500" data-testid="no-members">No people match</td></tr>}
          </tbody>
        </table>
        </div>
      </Section>

      {/* Edit employee dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="edit-employee-dialog">
          <DialogHeader><DialogTitle className="font-head">Edit Employee</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <div className="eyebrow mb-1">Email (read-only)</div>
              <Input value={editForm.email} disabled className="rounded-none bg-neutral-50" />
            </div>
            <div>
              <div className="eyebrow mb-1">Name</div>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-none" data-testid="edit-field-name" />
            </div>
            <div>
              <div className="eyebrow mb-1">Employee ID</div>
              <Input value={editForm.employee_id} onChange={(e) => setEditForm({ ...editForm, employee_id: e.target.value })} className="rounded-none" data-testid="edit-field-employee_id" />
            </div>
            <div>
              <div className="eyebrow mb-1">Designation</div>
              <Input value={editForm.designation} onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })} className="rounded-none" data-testid="edit-field-designation" />
            </div>
            <div>
              <div className="eyebrow mb-1">Phone</div>
              <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="rounded-none" data-testid="edit-field-phone" />
            </div>
            <OrgSelects value={editForm} onChange={setEditForm} depts={depts} teams={teams} admins={admins} prefix="edit-field" />
            <div>
              <div className="eyebrow mb-1">Joining Date</div>
              <Input type="date" value={editForm.joining_date} onChange={(e) => setEditForm({ ...editForm, joining_date: e.target.value })} className="rounded-none" data-testid="edit-field-joining_date" />
            </div>
            <div>
              <div className="eyebrow mb-1">Role</div>
              <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                className="w-full h-10 border border-[#E5E5E5] px-2" data-testid="edit-field-role">
                <option value="employee">Employee</option><option value="admin">Admin</option>{isSuper && <option value="super_admin">Super Admin</option>}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={onSaveEdit} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="save-edit-employee">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmSuper} onOpenChange={(v) => { if (!v) setConfirmSuper(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="confirm-super-admin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head">Grant full Super Admin access?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to give <span className="font-semibold text-[#1F4E8C]">{confirmSuper?.name}</span> full
              Super Admin access — complete control over every team's data, payroll, salaries, user management,
              settings and audit logs across the organization. This bypasses all team-level restrictions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="confirm-super-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-super-confirm"
              className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white"
              onClick={() => { const run = confirmSuper?.run; setConfirmSuper(null); run && run(); }}>
              Yes, grant Super Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={piOpen} onOpenChange={setPiOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="personal-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">
              Personal Information
              {piUser && <span className="text-sm font-normal text-neutral-500"> · {piUser.name} ({piUser.employee_id || piUser.email})</span>}
            </DialogTitle>
          </DialogHeader>
          {piLoading ? (
            <div className="text-neutral-500 text-sm py-8 text-center">Loading…</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-4 border-b border-[#F0F0EE] pb-4">
                <Avatar name={piUser?.name} photo={piPhoto} size={64} />
                <div>
                  <div className="font-head text-lg font-semibold">{piUser?.name}</div>
                  <div className="text-xs text-neutral-500 mb-2">{piUser?.email}</div>
                  <label className="inline-flex items-center h-8 px-3 border border-[#1F4E8C] text-[#1F4E8C] cursor-pointer hover:bg-[#1F4E8C] hover:text-white transition-colors text-xs font-semibold" data-testid="pi-upload-photo">
                    <Camera size={14} className="mr-1" /> {piUploading ? "Uploading…" : "Upload Photo"}
                    <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" disabled={piUploading} />
                  </label>
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2">Basic</div>
                <div className="grid grid-cols-2 gap-3">
                  {PI_BASIC.map((f) => (
                    <div key={f.k}>
                      <div className="eyebrow mb-1">{f.label}</div>
                      <Input type={f.type || "text"} value={piData[f.k] || ""} onChange={(e) => setPiData({ ...piData, [f.k]: e.target.value })} className="rounded-none" data-testid={`pi-${f.k}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="eyebrow mb-2">Contact & Address</div>
                <div className="grid grid-cols-2 gap-3">
                  {PI_CONTACT.map((f) => (
                    <div key={f.k}>
                      <div className="eyebrow mb-1">{f.label}</div>
                      <Input type={f.type || "text"} value={piData[f.k] || ""} onChange={(e) => setPiData({ ...piData, [f.k]: e.target.value })} className="rounded-none" data-testid={`pi-${f.k}`} />
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <div className="eyebrow mb-1">Current Address</div>
                  <Textarea value={piData.current_address || ""} onChange={(e) => setPiData({ ...piData, current_address: e.target.value })} className="rounded-none" rows={2} data-testid="pi-current_address" />
                </div>
                <div className="mt-3">
                  <div className="eyebrow mb-1">Permanent Address</div>
                  <Textarea value={piData.permanent_address || ""} onChange={(e) => setPiData({ ...piData, permanent_address: e.target.value })} className="rounded-none" rows={2} data-testid="pi-permanent_address" />
                </div>
              </div>

              <div>
                <div className="eyebrow mb-2">Employment</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="eyebrow mb-1">Employment Type</div>
                    <select value={piData.employment_type || ""} onChange={(e) => setPiData({ ...piData, employment_type: e.target.value })}
                      className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="pi-employment_type">
                      <option value="">—</option>
                      {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {PI_EMP.map((f) => (
                    <div key={f.k}>
                      <div className="eyebrow mb-1">{f.label}</div>
                      <Input type={f.type || "text"} value={piData[f.k] || ""} onChange={(e) => setPiData({ ...piData, [f.k]: e.target.value })} className="rounded-none" data-testid={`pi-${f.k}`} />
                    </div>
                  ))}
                </div>
              </div>

              {piCanEditPayroll && (
                <div>
                  <div className="eyebrow mb-2">Bank / Payment</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><div className="eyebrow mb-1">Bank Name</div><Input value={piBank.bank_name || ""} onChange={(e) => setPiBank({ ...piBank, bank_name: e.target.value })} className="rounded-none" data-testid="pi-bank_name" /></div>
                    <div><div className="eyebrow mb-1">Account Holder</div><Input value={piBank.account_holder || ""} onChange={(e) => setPiBank({ ...piBank, account_holder: e.target.value })} className="rounded-none" data-testid="pi-account_holder" /></div>
                    <div><div className="eyebrow mb-1">Account Number</div><Input value={piBank.account_number || ""} onChange={(e) => setPiBank({ ...piBank, account_number: e.target.value })} className="rounded-none" data-testid="pi-account_number" /></div>
                    <div><div className="eyebrow mb-1">IFSC / Routing</div><Input value={piBank.ifsc_code || ""} onChange={(e) => setPiBank({ ...piBank, ifsc_code: e.target.value })} className="rounded-none" data-testid="pi-ifsc_code" /></div>
                    <div><div className="eyebrow mb-1">Branch</div><Input value={piBank.branch || ""} onChange={(e) => setPiBank({ ...piBank, branch: e.target.value })} className="rounded-none" /></div>
                    <div><div className="eyebrow mb-1">UPI ID</div><Input value={piBank.upi_id || ""} onChange={(e) => setPiBank({ ...piBank, upi_id: e.target.value })} className="rounded-none" /></div>
                  </div>
                </div>
              )}

              {piCanEditPayroll && (
                <div>
                  <div className="eyebrow mb-2">Statutory</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><div className="eyebrow mb-1">PAN</div><Input value={piStatutory.pan || ""} onChange={(e) => setPiStatutory({ ...piStatutory, pan: e.target.value })} className="rounded-none" data-testid="pi-pan" /></div>
                    <div><div className="eyebrow mb-1">UAN</div><Input value={piStatutory.uan || ""} onChange={(e) => setPiStatutory({ ...piStatutory, uan: e.target.value })} className="rounded-none" data-testid="pi-uan" /></div>
                    <div><div className="eyebrow mb-1">PF Number</div><Input value={piStatutory.pf_number || ""} onChange={(e) => setPiStatutory({ ...piStatutory, pf_number: e.target.value })} className="rounded-none" /></div>
                    <div><div className="eyebrow mb-1">ESIC Number</div><Input value={piStatutory.esic_number || ""} onChange={(e) => setPiStatutory({ ...piStatutory, esic_number: e.target.value })} className="rounded-none" /></div>
                    <div>
                      <div className="eyebrow mb-1">Tax Regime</div>
                      <select value={piStatutory.tax_regime || "New"} onChange={(e) => setPiStatutory({ ...piStatutory, tax_regime: e.target.value })} className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="pi-tax_regime">
                        <option value="New">New</option><option value="Old">Old</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-3 gap-2 items-end">
                      <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!piStatutory.pf_applicable} onChange={(e) => setPiStatutory({ ...piStatutory, pf_applicable: e.target.checked })} /> PF</label>
                      <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!piStatutory.pt_applicable} onChange={(e) => setPiStatutory({ ...piStatutory, pt_applicable: e.target.checked })} /> PT</label>
                      <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={!!piStatutory.esic_applicable} onChange={(e) => setPiStatutory({ ...piStatutory, esic_applicable: e.target.checked })} /> ESIC</label>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <div className="eyebrow mb-2">Emergency Contacts</div>
                <div className="space-y-2">
                  {piEmergency.map((c) => (
                    <div key={c.id} className="flex items-center justify-between border border-[#E5E5E5] p-3 text-sm">
                      <div>
                        <div className="font-medium">{c.name} <span className="text-neutral-500 text-xs">· {c.relationship}</span></div>
                        <div className="text-xs text-neutral-500">{c.phone} · {c.email}</div>
                      </div>
                      <button onClick={() => delEmerAdmin(c.id)} className="text-neutral-500 hover:text-red-600" data-testid={`pi-del-emer-${c.id}`}><TrashIcon size={14} /></button>
                    </div>
                  ))}
                  {piEmergency.length === 0 && <div className="text-xs text-neutral-500">No emergency contacts yet.</div>}
                </div>
                <div className="mt-3 border-t border-[#E5E5E5] pt-3 grid grid-cols-2 gap-2">
                  <Input placeholder="Name" value={piNewEmer.name} onChange={(e) => setPiNewEmer({ ...piNewEmer, name: e.target.value })} className="rounded-none" data-testid="pi-emer-name" />
                  <Input placeholder="Relationship" value={piNewEmer.relationship} onChange={(e) => setPiNewEmer({ ...piNewEmer, relationship: e.target.value })} className="rounded-none" />
                  <Input placeholder="Phone" value={piNewEmer.phone} onChange={(e) => setPiNewEmer({ ...piNewEmer, phone: e.target.value })} className="rounded-none" />
                  <Input placeholder="Email" value={piNewEmer.email} onChange={(e) => setPiNewEmer({ ...piNewEmer, email: e.target.value })} className="rounded-none" />
                  <Button onClick={addEmerAdmin} className="col-span-2 rounded-none bg-[#1F4E8C] text-white h-9 text-sm" data-testid="pi-add-emer"><Plus size={14} className="mr-1" />Add contact</Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPiOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={saveProfile} disabled={piLoading || piSaving} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="save-personal-info">
              {piSaving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="delete-employee-dialog">
          <DialogHeader><DialogTitle className="font-head">Delete Employee</DialogTitle></DialogHeader>
          <div className="text-sm text-neutral-700 space-y-2">
            <p>Are you sure you want to permanently delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?</p>
            <p className="text-xs text-red-600">This removes the user account. Historical timesheets, attendance and payroll records will remain for audit, but this person will no longer be able to sign in.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-none">Cancel</Button>
            <Button onClick={confirmDelete} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-employee">Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
